/**
 * Global Token Metadata Cache with Persistence
 * 
 * This cache stores metadata (names, symbols, logos) discovered by the Nursery engine
 * or fetched via API fallbacks. It uses localStorage to maintain a persistent KV store
 * of discovered assets, minimizing redundant API calls and providing zero-latency UI.
 */

export type CachedMeta = {
  mint: string;
  name: string;
  symbol: string;
  imageUri: string | null;
  description?: string | null;
  twitter?: string | null;
  telegram?: string | null;
  website?: string | null;
  creator?: string | null;
  replyCount?: number | null;
  createdTimestamp?: number | null;
};

const CACHE_KEY = "solclaw_token_meta_v1";
const MAX_CACHE_SIZE = 500;

// Initialize cache from localStorage
let internalCache: Map<string, CachedMeta>;
try {
  const saved = localStorage.getItem(CACHE_KEY);
  if (saved) {
    const parsed = JSON.parse(saved);
    internalCache = new Map(Object.entries(parsed));
  } else {
    internalCache = new Map();
  }
} catch {
  internalCache = new Map();
}

function persist() {
  try {
    // Keep cache size manageable
    if (internalCache.size > MAX_CACHE_SIZE) {
      const keys = Array.from(internalCache.keys());
      for (let i = 0; i < keys.length - MAX_CACHE_SIZE; i++) {
        internalCache.delete(keys[i]);
      }
    }
    const obj = Object.fromEntries(internalCache);
    localStorage.setItem(CACHE_KEY, JSON.stringify(obj));
  } catch (err) {
    console.error("Failed to persist token meta cache:", err);
    if (err instanceof DOMException && err.name === 'QuotaExceededError') {
      try {
        localStorage.removeItem(CACHE_KEY);
        internalCache.clear();
      } catch (e) {
        // ignore
      }
    }
  }
}

export const tokenMetaCache = {
  get(mint: string): CachedMeta | undefined {
    return internalCache.get(mint);
  },

  set(mint: string, meta: Partial<CachedMeta> & { mint: string }) {
    const existing = internalCache.get(mint) || { mint, name: "", symbol: "", imageUri: null };
    internalCache.set(mint, {
      ...existing,
      ...meta,
    });
    persist();
  },

  /**
   * Prime the cache from high-fidelity sources like the Nursery stream or Signals.
   */
  primeFromNursery(meta: CachedMeta) {
    const existing = internalCache.get(meta.mint);
    
    // Quality check: is the existing name just a placeholder?
    const isPlaceholder = !existing || 
      existing.name.startsWith("Token ") || 
      existing.name.startsWith("Indexing ") ||
      existing.name === "Pump.fun Asset" ||
      existing.name === "-" ||
      existing.name === "?";

    // Update if we have no data, if existing data is low quality,
    // or if the new data provides a logo we were missing.
    if (isPlaceholder || (!existing?.imageUri && meta.imageUri)) {
      internalCache.set(meta.mint, {
        ...existing,
        ...meta
      });
      persist();
    }
  },

  getAll(): CachedMeta[] {
    return Array.from(internalCache.values());
  }
};
